import type { ContinuityRecordKind, ContinuityRecordSummary } from "@/lib/behavior/continuityRecord";
import type {
  LeadershipRecordItem,
  LeadershipRecordSourceType,
} from "@/lib/behavior/viewModels/buildLeadershipRecordItems";
import { isLeaderVisibleRecordItem } from "@/lib/behavior/viewModels/buildLeadershipRecordItems";

type RecordSource =
  | "transcript_upload_processed"
  | "reflection_submitted"
  | "leader_synthesis_finalized"
  | "manual_reflection_submitted"
  | "transcript_review_completed"
  | "coaching_note_finalized"
  | "coaching_synthesis_approved"
  | "coaching_reflection_saved"
  | string;

export interface ContinuityRecordMomentInput {
  id: string;
  source: RecordSource;
  sourceTable?: string | null;
  sourceId?: string | null;
  occurredAt?: string | null;
  title?: string | null;
  excerpt?: string | null;
}

export interface ContinuityRecordSignalInput {
  label: string;
  detail?: string | null;
  href?: string | null;
}

export interface ContinuityRecordTextInput {
  id: string;
  text: string;
  occurredAt?: string | null;
  href?: string | null;
}

export interface ContinuityRecordExperience {
  title: "Leadership Record" | "Coaching Record";
  subtitle: string;
  counterText: string;
  accumulationText: string;
  empty: boolean;
  momentHistory: Array<{
    id: string;
    label: string;
    title: string;
    excerpt: string | null;
    occurredAt: string | null;
    evidenceHref: string | null;
  }>;
  normalizedItems?: LeadershipRecordItem[];
  recurringThemes: ContinuityRecordSignalInput[];
  emergingTensions: ContinuityRecordSignalInput[];
  shiftsOverTime: string[];
  recentReflections: ContinuityRecordTextInput[];
  recentActivity: ContinuityRecordTextInput[];
  lensContinuity: {
    title: string;
    body: string;
  } | null;
}

export interface BuildContinuityRecordExperienceInput {
  kind: ContinuityRecordKind;
  summary: ContinuityRecordSummary;
  relationshipId?: string | null;
  relationshipName?: string | null;
  moments?: ContinuityRecordMomentInput[];
  leadershipRecordItems?: LeadershipRecordItem[];
  themes?: ContinuityRecordSignalInput[];
  tensions?: ContinuityRecordSignalInput[];
  reflections?: ContinuityRecordTextInput[];
  coachingActivity?: ContinuityRecordTextInput[];
  shifts?: string[];
  lens?: {
    title: string;
    body: string;
  } | null;
}

const LEADERSHIP_RECORD_SOURCE_LABELS: Record<LeadershipRecordSourceType, string> = {
  bi_observation: "Behavior evidence",
  context_graph_node: "Context graph node",
  leader_memory_event: "Leader memory event",
  practice_summary: "Practice summary",
  transcript_evidence: "Transcript evidence",
  reflection: "Reflection",
  coach_note: "Coach note",
  shared_summary: "Shared summary",
};

function normalizeText(value?: string | null): string | null {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized || null;
}

function buildRecordItemEvidenceHref(params: {
  kind: ContinuityRecordKind;
  relationshipId?: string | null;
  item: LeadershipRecordItem;
}): string | null {
  if (params.kind === "leadership") {
    if (params.item.sourceType === "transcript_evidence") return `/transcript-analysis/${params.item.sourceId}`;
    if (params.item.sourceType === "reflection" || params.item.sourceType === "shared_summary") {
      return `/post-meeting/${params.item.sourceId}`;
    }
    return `/leadership-record?moment=${params.item.sourceId}`;
  }
  if (!params.relationshipId) return "/coach/record";
  if (params.item.sourceType === "coach_note" || params.item.sourceType === "leader_memory_event") {
    return `/coach/leaders/${params.relationshipId}/record?moment=${params.item.sourceId}`;
  }
  return `/coach/leaders/${params.relationshipId}?section=evidence`;
}

function sourceLabel(kind: ContinuityRecordKind, source: RecordSource): string {
  if (kind === "coaching") return "Coaching memory update";
  if (source === "transcript_upload_processed") return "Leadership record update";
  if (source === "leader_synthesis_finalized") return "Leadership synthesis finalized";
  return "Leadership record update";
}

function fallbackTitle(source: RecordSource): string {
  switch (source) {
    case "transcript_upload_processed":
      return "Transcript processed";
    case "reflection_submitted":
      return "Reflection saved";
    case "leader_synthesis_finalized":
      return "Synthesis finalized";
    case "manual_reflection_submitted":
      return "Reflection saved";
    case "transcript_review_completed":
      return "Transcript review completed";
    case "coaching_note_finalized":
      return "Coaching note finalized";
    case "coaching_synthesis_approved":
      return "Coaching synthesis approved";
    case "coaching_reflection_saved":
      return "Coaching reflection saved";
    default:
      return "Record update";
  }
}

function buildEvidenceHref(params: {
  kind: ContinuityRecordKind;
  relationshipId?: string | null;
  sourceTable?: string | null;
  sourceId?: string | null;
}): string | null {
  if (!params.sourceId) return null;
  if (params.kind === "leadership") {
    if (params.sourceTable === "transcript_uploads") return `/transcript-analysis/${params.sourceId}`;
    if (params.sourceTable === "post_meeting_reflections") return `/post-meeting/${params.sourceId}`;
    return `/leadership-record?moment=${params.sourceId}`;
  }
  if (!params.relationshipId) return "/coach/record";
  if (params.sourceTable === "coach_leader_session_records") {
    return `/coach/leaders/${params.relationshipId}?section=coach-growth&id=${params.sourceId}`;
  }
  return `/coach/leaders/${params.relationshipId}/record?moment=${params.sourceId}`;
}

function buildDefaultShifts(params: {
  kind: ContinuityRecordKind;
  count: number;
  relationshipName?: string | null;
  moments: ContinuityRecordMomentInput[];
}): string[] {
  if (params.moments.length === 0) {
    return [
      params.kind === "leadership"
        ? "Your leadership arc will become clearer as more record updates accumulate."
        : "Your coaching arc will become clearer as more Coaching Memory updates accumulate.",
    ];
  }
  const latest = normalizeText(params.moments[0]?.title) || fallbackTitle(params.moments[0]?.source || "");
  if (params.kind === "coaching" && params.relationshipName) {
    return [`Recent coaching continuity with ${params.relationshipName} is anchored by ${latest}.`];
  }
  return [`Recent continuity is anchored by ${latest}.`];
}

export function buildContinuityRecordExperience(
  input: BuildContinuityRecordExperienceInput,
): ContinuityRecordExperience {
  const relationshipName = normalizeText(input.relationshipName);
  const hasNormalizedInputItems = Boolean(input.leadershipRecordItems);
  const normalizedInputItems = input.kind === "leadership"
    ? (input.leadershipRecordItems || []).filter(isLeaderVisibleRecordItem)
    : (input.leadershipRecordItems || []);
  const normalizedItems = hasNormalizedInputItems
    ? [...normalizedInputItems].sort((a, b) =>
        String(b.occurredAt || b.createdAt || "").localeCompare(String(a.occurredAt || a.createdAt || "")),
      )
    : undefined;
  const moments = normalizedItems
    ? normalizedItems.map((item) => ({
        id: item.id,
        source: item.sourceType,
        sourceTable: null,
        sourceId: item.sourceId,
        occurredAt: item.occurredAt || item.createdAt,
        title: item.title,
        excerpt: item.observedFact,
      }))
    : [...(input.moments || [])].sort((a, b) =>
    String(b.occurredAt || "").localeCompare(String(a.occurredAt || "")),
  );
  const title = input.kind === "leadership" ? "Leadership Record" : "Coaching Record";
  const subtitle = input.kind === "leadership"
    ? "A reflective record of leadership updates, themes, tensions, and shifts over time."
    : relationshipName
      ? `A reflective record of coaching continuity with ${relationshipName}.`
      : "A reflective record of Coaching Memory updates, themes, touchpoints, and shifts over time.";

  return {
    title,
    subtitle,
    counterText: input.summary.counterText,
    accumulationText: input.summary.accumulationText,
    empty: moments.length === 0,
    momentHistory: moments.map((moment) => ({
      id: moment.id,
      label: normalizedItems
        ? LEADERSHIP_RECORD_SOURCE_LABELS[moment.source as LeadershipRecordSourceType]
        : sourceLabel(input.kind, moment.source),
      title: normalizeText(moment.title) || fallbackTitle(moment.source),
      excerpt: normalizeText(moment.excerpt),
      occurredAt: moment.occurredAt || null,
      evidenceHref: normalizedItems
        ? buildRecordItemEvidenceHref({
            kind: input.kind,
            relationshipId: input.relationshipId,
            item: normalizedItems.find((item) => item.id === moment.id)!,
          })
        : buildEvidenceHref({
            kind: input.kind,
            relationshipId: input.relationshipId,
            sourceTable: moment.sourceTable,
            sourceId: moment.sourceId,
          }),
    })),
    ...(normalizedItems ? { normalizedItems } : {}),
    recurringThemes: (input.themes || []).slice(0, 6),
    emergingTensions: (input.tensions || []).slice(0, 6),
    shiftsOverTime: (input.shifts && input.shifts.length > 0)
      ? input.shifts
      : buildDefaultShifts({
          kind: input.kind,
          count: input.summary.count,
          relationshipName,
          moments,
        }),
    recentReflections: (input.reflections || []).slice(0, 5),
    recentActivity: (input.coachingActivity || []).slice(0, 5),
    lensContinuity: input.lens && normalizeText(input.lens.body)
      ? {
          title: normalizeText(input.lens.title) || "Philosophy continuity",
          body: normalizeText(input.lens.body) || "",
        }
      : null,
  };
}
